import { useEffect, useMemo, useRef, useState } from "react";
import MainHeroBanner from "../../assets/images/spinner/radial-bg.svg";
import { useTranslation } from "react-i18next";
import PrimaryBtn from "../button/PrimaryBtn";
import useToast from "../../hooks/useToast";
import { VscTriangleDown } from "react-icons/vsc";

interface Lifeline {
  id: string;
  name: string;
  image: string;
  description: string;
  status?: boolean;
}

interface SpinWheelModalProps {
  isOpen: boolean;
  lifelines: Lifeline[];
  teams: { team_name: string; participants?: any[]; lifelines?: string[] }[];
  lifelinesPerTeam: number;
  onComplete: (teamLifelines: { teamIndex: number; lifelineIds: string[] }[]) => void;
  onClose: () => void;
  isCreatingGame: boolean;
  embedded?: boolean;
  lifelineEnabled?: boolean;
  onLifelineEnabledChange?: (enabled: boolean) => void;
}
interface TeamCardProps {
  title?: string;
  color: string;
  items: Array<Lifeline | null>;
  isManual: boolean;
  onAdd: (teamIndex: number, slotIndex: number) => void;
  onRemove: (teamIndex: number, slotIndex: number) => void;
  teamIndex: number;
}

const LifelineSelectionModal = ({
  isOpen,
  onClose,
  lifelines,
  onSelectLifelineId,
  selectedLifelineId,
  onConfirm,
  selectedLifelineIds,
  allowMultiple = false,
  maxSelections = 3,
  currentlySelectedIds = [],
}: {
  isOpen: boolean;
  onClose: () => void;
  lifelines: Lifeline[];
  onSelectLifelineId: (lifelineId: string) => void;
  selectedLifelineId: string | null;
  onConfirm: () => void;
  selectedLifelineIds: string[]; // Already assigned lifelines in the team (to prevent duplicates)
  allowMultiple?: boolean;
  maxSelections?: number;
  currentlySelectedIds?: string[]; // Currently selected lifelines in the modal
}) => {
  if (!isOpen) return null;

  const { t } = useTranslation();

  // When some lifelines are already assigned to the team, only allow
  // selecting the remaining number of slots for this team in the modal.
  const remainingSlots =
    allowMultiple && maxSelections > selectedLifelineIds.length
      ? maxSelections - selectedLifelineIds.length
      : allowMultiple
        ? 0
        : maxSelections;

  const currentSelectedIds = allowMultiple ? currentlySelectedIds : selectedLifelineId ? [selectedLifelineId] : [];

  const effectiveMaxSelections = allowMultiple ? remainingSlots : maxSelections;
  const canSelectMore = effectiveMaxSelections > 0 && currentSelectedIds.length < effectiveMaxSelections;

  return (
    <div className="fixed inset-0 bg-black/80 flex items-center justify-center lg:p-[1%] p-4 z-40">
      <div
        // style={{ backgroundImage: `url(${MainHeroBanner})` }}
        className="bg-center bg-cover modal bg-primary-skyblue 2xl:rounded-[2.083vw] md:rounded-[40px] sm:rounded-4xl rounded-2xl 2xl:p-[2.083vw] md:p-7 p-4 2xl:max-w-[45.833vw] max-w-[600px]  w-full relative animate-[slideUp_0.3s_ease-out] 2xl:border-[0.2vw] border-3 border-model-border overflow-auto 2xl:max-h-[45.833vw] max-h-[600px]  hide-scroll flex flex-col justify-between"
      >
        {/* <button
          onClick={onClose}
          className="absolute top-4 right-4 w-6 h-6 flex items-center justify-center hover:scale-110 transition-transform"
        >
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
            <path d="M18 6L6 18M6 6l12 12" stroke="#333" strokeWidth="2" strokeLinecap="round"/>
          </svg>
        </button> */}

        <div className="flex items-center justify-between w-full 2xl:mb-[1vw] mb-2">
          <h2 className="2xl:text-[1.563vw]/[1.7vw] xl:text-3xl/tight text-2xl/6 font-semibold text-primary-orange 2xl:mb-[1vw]  text-start ">
            {allowMultiple
              ? t("createGame.selectLifelines") || "Select Lifelines"
              : t("createGame.lifeline") || "Lifeline"}
          </h2>
          <button
            onClick={onClose}
            className="lg:w-[1.2vw] lg:h-[1.2vw] md:w-5 w-4 md:h-5 h-4 flex items-center justify-center cursor-pointer ms-auto hover:scale-110 transition-transform duration-300 outline-none ml-4"
          >
            <svg
              className="w-full h-full"
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M15.9474 12.1123C15.8856 12.0504 15.8856 11.9496 15.9474 11.8878L23.5029 4.3286C24.149 3.68231 24.149 2.63063 23.5029 1.98429L22.0051 0.48573C21.692 0.172468 21.2758 0 20.833 0C20.3902 0 19.9739 0.172468 19.6609 0.485683L12.1054 8.04493C12.0672 8.08316 12.0235 8.09122 11.9937 8.09122C11.9639 8.09122 11.9203 8.08321 11.8821 8.04493L4.32658 0.485683C4.01351 0.172468 3.59726 0 3.15445 0C2.71164 0 2.29544 0.172468 1.98236 0.485683L0.484547 1.98429C-0.161516 2.63063 -0.161516 3.68231 0.484547 4.3286L8.04001 11.8878C8.10185 11.9496 8.10185 12.0504 8.04001 12.1123L0.484547 19.6714C-0.161469 20.3178 -0.161469 21.3694 0.484547 22.0157L1.98236 23.5143C2.29544 23.8275 2.71168 24 3.15445 24C3.59721 24 4.01351 23.8275 4.32658 23.5143L11.882 15.9551C11.9203 15.9169 11.9638 15.9089 11.9937 15.9089C12.0235 15.9089 12.0671 15.9169 12.1053 15.9552L19.6608 23.5143C19.9739 23.8276 20.3901 24 20.8329 24C21.2758 24 21.692 23.8276 22.005 23.5143L23.5028 22.0158C24.1489 21.3695 24.1489 20.3178 23.5028 19.6715L15.9474 12.1123Z"
                fill="#333938"
              />
            </svg>
          </button>
        </div>

        {allowMultiple && (
          <p className="text-center  text-black-900 2xl:mb-[0.5vw] mb-2">
            {t("createGame.selectUpTo", { count: remainingSlots }) || `Select up to ${remainingSlots} lifelines`}
            {/* ({currentSelectedIds.length}/{remainingSlots}) */}
          </p>
        )}
        <div className="text-center 2xl:mb-[1vw] mb-5">
          <hr className="border-0 2xl:h-[0.053vw] h-[1px] w-full [background:linear-gradient(90deg,rgba(11,51,105,0)_0%,#7364FF_50%,rgba(11,51,105,0)_100%)]" />
        </div>

        <div className="grid grid-cols-3 2xl:gap-[0.417vw] gap-2 flex-1 2xl:mb-[0.8vw] mb-3">
          {lifelines.map((lifeline) => {
            const isAlreadySelected = selectedLifelineIds.includes(lifeline.id);
            const isCurrentlySelected = allowMultiple
              ? currentSelectedIds.includes(lifeline.id)
              : selectedLifelineId === lifeline.id;
            const isDisabled =
              isAlreadySelected ||
              (!allowMultiple && isAlreadySelected) ||
              (allowMultiple && !canSelectMore && !isCurrentlySelected);
            return (
              <div key={lifeline.id} className="relative group">
                <button
                  onClick={() => {
                    if (allowMultiple) {
                      // Toggle selection for multiple mode
                      if (isCurrentlySelected) {
                        // Deselect
                        const newIds = currentSelectedIds?.filter((id) => id !== lifeline.id);
                        // We need to handle this differently - update the parent's selectedLifelineIds
                        // For now, we'll use onSelectLifelineId to toggle
                        onSelectLifelineId(lifeline.id);
                      } else if (canSelectMore && !isAlreadySelected) {
                        // Select
                        onSelectLifelineId(lifeline.id);
                      }
                    } else {
                      // Single selection mode
                      if (!isAlreadySelected) {
                        onSelectLifelineId(lifeline.id);
                      }
                    }
                  }}
                  disabled={isDisabled}
                  className={`w-full h-full 2xl:min-h-[6.146vw] xl:min-h-[103px] min-h-20 2xl:rounded-[0.417vw] rounded-lg 2xl:p-[0.625vw] p-2 2xl:border-[0.104vw] border-[1.5px] flex flex-col items-center justify-center min-h-[80px] relative ${isDisabled
                    ? "bg-gray-200 border-gray-400 opacity-50 cursor-not-allowed"
                    : isCurrentlySelected
                      ? "bg-primary-light-orange border-primary-orange cursor-pointer"
                      : "bg-white border-black-300 cursor-pointer hover:bg-primary-yellow"
                    }`}
                >
                  {allowMultiple && isCurrentlySelected && (
                    <div className="absolute 2xl:top-[0.3vw] top-1 2xl:right-[0.3vw] right-1 2xl:w-[1.25vw] 2xl:h-[1.25vw] w-5 h-5 bg-primary-orange rounded-full flex items-center justify-center">
                      <svg
                        className="2xl:w-[0.833vw] 2xl:h-[0.833vw] w-3.5 h-3.5 text-white"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                  )}
                  <img
                    src={lifeline.image}
                    alt={lifeline.name}
                    className={`2xl:w-[3.125vw] md:w-10 w-8 2xl:h-[3.125vw] md:h-10 h-8 object-contain 2xl:mb-[0.625vw] mb-2 ${isDisabled ? "grayscale" : ""}`}
                  />
                  <span className="2xl:text-[0.833vw]/tight xl:text-base/tight md:text-sm/tight text-xs/tight font-bold text-center">
                    {lifeline.name}
                  </span>
                </button>

              </div>
            );
          })}
        </div>
        <PrimaryBtn onClick={onConfirm} disabled={currentSelectedIds.length === 0} className="!mb-0 mx-auto h-fit">
          {t("createGame.selectLifelines")}
        </PrimaryBtn>
      </div>
    </div>
  );
};

const TeamCard = ({ title, color, items, isManual, onAdd, onRemove, teamIndex }: TeamCardProps) => {
  const { t } = useTranslation();
  return (
    <div
      className={`${color} 2xl:rounded-[0.833vw] rounded-2xl 2xl:p-[0.833vw] p-4 2xl:border-[0.104vw] border-2 2xl:shadow-[0px_0.156vw_0px_#110706] shadow-[0px_2px_0px_#110706] flex flex-col`}
    >
      {title ? (
        <>
          <h3 className="text-center font-bold 2xl:text-[1.25vw]/tight text-xl/tight text-primary-rose 2xl:pb-[0.5vw] pb-2">
            {title}
          </h3>
          {/* Divider */}
          <div className="text-center 2xl:mb-[0.8vw] mb-3">
            <hr className="border-0 2xl:h-[0.053vw] h-[1px] w-full [background:linear-gradient(90deg,rgba(11,51,105,0)_0%,#7364FF_50%,rgba(11,51,105,0)_100%)]" />
          </div>
        </>
      ) : null}
      <div className="grid grid-cols-3 2xl:gap-[0.417vw] gap-2 flex-1">
        {items.map((item, idx) => (
          <div
            key={idx}
            className="2xl:min-h-[6.146vw] xl:min-h-[103px] min-h-20 bg-white 2xl:rounded-[0.417vw] rounded-lg 2xl:p-[0.625vw] p-2 2xl:border-[0.104vw] border-[1.5px] border-black-300 flex flex-col items-center justify-center min-h-[80px] relative cursor-pointer group"
          >
            {item ? (
              <>
                {isManual && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onRemove(teamIndex, idx);
                    }}
                    className="absolute 2xl:top-[0.3vw] top-0.5 2xl:right-[0.3vw] right-0.5 2xl:w-[1.25vw] 2xl:h-[1.25vw] w-5 h-5 flex items-center justify-center bg-primary-orange rounded-full transition-colors cursor-pointer z-10"
                    title="Remove lifeline"
                  >
                    <svg
                      className="2xl:w-[0.833vw] 2xl:h-[0.833vw] w-3.5 h-3.5"
                      width="14"
                      height="14"
                      viewBox="0 0 14 14"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M10.5 3.5L3.5 10.5M3.5 3.5L10.5 10.5"
                        stroke="white"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </button>
                )}
                <img
                  src={item.image}
                  alt={item.name}
                  className="2xl:w-[3.125vw] md:w-10 w-8 2xl:h-[3.125vw] md:h-10 h-8 object-contain 2xl:mb-[0.625vw] mb-1"
                />
                <span className="2xl:text-[0.833vw]/tight xl:text-base/tight md:text-sm/tight text-xs/tight font-bold text-center">
                  {item.name}
                </span>
              </>
            ) : isManual ? (
              <button
                className="flex flex-col items-center w-full cursor-pointer"
                onClick={(e) => {
                  e.stopPropagation();
                  onAdd(teamIndex, idx);
                }}
              >
                <span className="2xl:mb-[0.625vw] mb-2.5 flex items-center justify-center">
                  <svg
                    className="2xl:w-[1.25vw] 2xl:h-[1.25vw] w-5 h-5"
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M10.2857 13.7143H0V10.2857H10.2857V0H13.7143V10.2857H24V13.7143H13.7143V24H10.2857V13.7143Z"
                      fill="#110706"
                    />
                  </svg>
                </span>
                <span className="2xl:text-[0.833vw]/tight xl:text-base/tight md:text-sm/tight text-xs/tight font-bold text-center">
                  {t("createGame.add")}
                </span>
              </button>
            ) : null}
          </div>
        ))}
      </div>
    </div>
  );
};

const pickRandomUnique = <T,>(items: T[], count: number): T[] => {
  const copy = [...items];
  for (let i = copy.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [copy[i], copy[j]] = [copy[j], copy[i]];
  }
  return copy.slice(0, Math.min(count, copy.length));
};

const SpinWheelModal = ({
  isOpen,
  lifelines,
  teams,
  lifelinesPerTeam,
  onComplete,
  isCreatingGame,
  onClose,
  embedded = false,
  lifelineEnabled = true,
  onLifelineEnabledChange,
}: SpinWheelModalProps) => {
  const { t } = useTranslation();

  useEffect(() => {
    if (embedded) {
      document.body.style.overflow = "unset";
      return;
    }

    if (isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }

    return () => {
      document.body.style.overflow = "unset";
    };
  }, [isOpen]);

  // if (!isOpen) return null;

  const enabledLifelines = useMemo(() => lifelines?.filter((l) => l.status !== false), [lifelines]);
  const lifelineById = useMemo(() => new Map(enabledLifelines.map((l) => [l.id, l])), [enabledLifelines]);

  const [mode, setMode] = useState<"random" | "manual" | "without">("manual");
  const [isSelectionModalOpen, setIsSelectionModalOpen] = useState(false);
  const [selectedLifelineId, setSelectedLifelineId] = useState<string | null>(null);
  const [selectedLifelineIds, setSelectedLifelineIds] = useState<string[]>([]);
  const [currentSelection, setCurrentSelection] = useState<{ teamIndex: number; slotIndex: number } | null>(null);
  const [activeLifelineDescriptionId, setActiveLifelineDescriptionId] = useState<string | null>(null);

  const [teamLifelines, setTeamLifelines] = useState<(Lifeline | null)[][]>([]);
  const presetLifelinesAppliedRef = useRef(false);
  const skipPresetHydrationRef = useRef(false);
  const lastEmbeddedSyncKeyRef = useRef("");

  const displayTeamCount = Math.max(teams.length, 1);
  const makeEmptySlots = () =>
    Array.from({ length: displayTeamCount }, () =>
      Array.from({ length: lifelinesPerTeam }, () => null as Lifeline | null)
    );
  const makeRandomSlots = () => {
    const picked = pickRandomUnique(enabledLifelines, lifelinesPerTeam);
    return Array.from({ length: lifelinesPerTeam }, (_, i) => picked[i] ?? null) as (Lifeline | null)[];
  };

  const assignRandomToAllTeams = () => {
    setTeamLifelines(
      Array.from({ length: displayTeamCount }, () => {
        const picked = pickRandomUnique(enabledLifelines, lifelinesPerTeam);
        const slots: (Lifeline | null)[] = Array.from({ length: lifelinesPerTeam }, (_, i) => picked[i] ?? null);
        return slots;
      })
    );
  };

  const clearAllLifelineState = () => {
    setTeamLifelines(makeEmptySlots());
    setIsSelectionModalOpen(false);
    setSelectedLifelineId(null);
    setSelectedLifelineIds([]);
    setCurrentSelection(null);
    setActiveLifelineDescriptionId(null);
  };

  useEffect(() => {
    if (!isOpen) return;
    // Initialize once when modal is opened.
    presetLifelinesAppliedRef.current = false;
    skipPresetHydrationRef.current = false;
    setMode(lifelineEnabled ? "manual" : "without");
    setTeamLifelines(makeEmptySlots());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    if (!lifelineEnabled) {
      setMode("without");
      setTeamLifelines(makeEmptySlots());
      return;
    }
    // If user toggles back from "without", move to manual once.
    setMode((prev) => (prev === "without" ? "manual" : prev));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lifelineEnabled, isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    if (skipPresetHydrationRef.current) return;
    // Do not force manual preset hydration after user switches mode.
    if (mode !== "manual") return;
    // Hydrate template/play-again lifelines whenever they are available.
    const presetTeamLifelinesIds = teams.map((team) => (Array.isArray(team.lifelines) ? team.lifelines : []));
    const hasPreset = presetTeamLifelinesIds.some((ids) => ids && ids.length > 0);
    if (!hasPreset) return;
    const hasLocalAssignments = teamLifelines.some((slots) => slots.some((slot) => slot !== null));
    // Don't override user-selected mode/state once local lifelines already exist.
    if (hasLocalAssignments) return;

    presetLifelinesAppliedRef.current = true;
    setMode("manual");
    setTeamLifelines(
      presetTeamLifelinesIds.map((ids) =>
        Array.from({ length: lifelinesPerTeam }, (_, slotIndex) => {
          const id = ids?.[slotIndex];
          if (!id) return null;
          return lifelineById.get(id) ?? null;
        })
      )
    );
  }, [isOpen, teams, lifelineById, lifelinesPerTeam, teamLifelines, mode]);

  useEffect(() => {
    if (!isOpen) return;
    setTeamLifelines((prev) => {
      const targetTeams = displayTeamCount;
      if (prev.length === targetTeams) return prev;

      // Team removed: trim extra teams.
      if (targetTeams < prev.length) return prev.slice(0, targetTeams);

      // Team added: keep existing team lifelines and append by mode.
      const addedCount = targetTeams - prev.length;
      const newRows = Array.from({ length: addedCount }, () =>
        mode === "random" && enabledLifelines.length > 0
          ? makeRandomSlots()
          : Array.from({ length: lifelinesPerTeam }, () => null as Lifeline | null)
      );
      return [...prev, ...newRows];
    });
  }, [isOpen, displayTeamCount, mode, enabledLifelines, lifelinesPerTeam]);

  useEffect(() => {
    if (!isOpen) return;
    if (presetLifelinesAppliedRef.current) return;
    if (mode === "random") {
      if (enabledLifelines.length > 0) assignRandomToAllTeams();
      else setTeamLifelines(makeEmptySlots());
    }
    if (mode === "manual") {
      setTeamLifelines(makeEmptySlots());
    }
    if (mode === "without") {
      setTeamLifelines(makeEmptySlots());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode]);

  useEffect(() => {
    onLifelineEnabledChange?.(mode !== "without");
    // Intentionally only react to mode transitions.
    // Parent passes an inline callback whose identity changes per render.
    // Including it in deps can create update loops.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode]);

  const handleAdd = (teamIndex: number, slotIndex: number) => {
    setCurrentSelection({ teamIndex, slotIndex });
    setSelectedLifelineId(null);
    setSelectedLifelineIds([]);
    setIsSelectionModalOpen(true);
  };

  const handleToggleLifelineSelection = (lifelineId: string) => {
    if (mode === "manual") {
      // Multiple selection mode
      setSelectedLifelineIds((prev) => {
        if (prev.includes(lifelineId)) {
          // Deselect
          return prev?.filter((id) => id !== lifelineId);
        } else if (prev?.length < lifelinesPerTeam) {
          // Select (up to max)
          return [...prev, lifelineId];
        }
        return prev;
      });
    } else {
      // Single selection mode (for backward compatibility)
      setSelectedLifelineId(lifelineId);
    }
  };

  const handleConfirmLifeline = () => {
    if (!currentSelection) return;

    if (mode === "manual" && selectedLifelineIds?.length > 0) {
      // Multiple selection mode - fill all empty slots
      const selectedLifelines = selectedLifelineIds
        .map((id) => enabledLifelines?.find((l) => l.id === id))
        .filter(Boolean) as Lifeline[];

      if (selectedLifelines.length === 0) return;

      setTeamLifelines((prev) => {
        const currentTeamSlots = prev[currentSelection.teamIndex] || [];
        const emptySlotIndices: number[] = [];
        const newSlots = currentTeamSlots.map((slot, sIdx) => {
          if (!slot) {
            emptySlotIndices.push(sIdx);
            return null;
          }
          return slot;
        });

        // Fill empty slots with selected lifelines
        selectedLifelines.forEach((lifeline, idx) => {
          if (emptySlotIndices[idx] !== undefined) {
            newSlots[emptySlotIndices[idx]] = lifeline;
          }
        });

        return prev.map((slots, tIdx) => (tIdx !== currentSelection.teamIndex ? slots : newSlots));
      });
    } else if (selectedLifelineId) {
      // Single selection mode
      const lifeline = enabledLifelines?.find((l) => l.id === selectedLifelineId);
      if (!lifeline) return;

      setTeamLifelines((prev) =>
        prev.map((slots, tIdx) =>
          tIdx !== currentSelection.teamIndex
            ? slots
            : slots.map((slot, sIdx) => (sIdx === currentSelection.slotIndex ? lifeline : slot))
        )
      );
    } else {
      return; // No selection
    }

    setIsSelectionModalOpen(false);
    setSelectedLifelineId(null);
    setSelectedLifelineIds([]);
    setCurrentSelection(null);
  };

  const handleRemove = (teamIndex: number, slotIndex: number) => {
    setTeamLifelines((prev) =>
      prev.map((slots, tIdx) =>
        tIdx !== teamIndex ? slots : slots.map((slot, sIdx) => (sIdx === slotIndex ? null : slot))
      )
    );
  };

  // Get already assigned lifeline IDs for the current team (to prevent duplicates)
  // This allows the same lifeline to be selected by different teams, but not twice in the same team
  const alreadyAssignedLifelineIds = useMemo(() => {
    if (!currentSelection) return [];
    const ids: string[] = [];
    const currentTeamSlots = teamLifelines[currentSelection.teamIndex] || [];
    currentTeamSlots.forEach((slot) => {
      if (slot) {
        ids.push(slot.id);
      }
    });
    return ids;
  }, [teamLifelines, currentSelection]);

  const teamCards = useMemo(
    () =>
      Array.from({ length: displayTeamCount }, (_, index) => ({
        title: teams[index]?.team_name || "",
        color: index % 2 === 0 ? "bg-primary-ivory" : "bg-primary-yellow",
      })),
    [displayTeamCount, teams]
  );

  const handleGoToGamePlay = () => {
    const payload = teams.map((_, teamIndex) => ({
      teamIndex,
      lifelineIds: (teamLifelines?.[teamIndex] || []).filter(Boolean).map((l) => (l as Lifeline).id),
    }));

    if (teamLifelines?.some((team) => team.some((lifeline) => lifeline === null))) {
      useToast(t("createGame.error.pleaseAddAtLeast3LifelinesToAllTeams"), "error");
      return;
    }

    onComplete(payload);
  };

  useEffect(() => {
    if (!embedded || teams.length === 0) return;

    const payload = teams.map((_, teamIndex) => ({
      teamIndex,
      lifelineIds: (teamLifelines?.[teamIndex] || []).filter(Boolean).map((l) => (l as Lifeline).id),
    }));

    const hasPresetLifelines = teams.some(
      (team) => Array.isArray(team.lifelines) && team.lifelines.length > 0
    );
    const hasLocalAssignments = teamLifelines.some((slots) =>
      slots.some((slot) => slot !== null)
    );

    // Play Again: parent already has preset lifelines, but local UI is still empty.
    // Avoid syncing empty lifelines back to parent before preset hydration finishes.
    if (hasPresetLifelines && !hasLocalAssignments) return;

    const syncKey = JSON.stringify(payload);
    if (lastEmbeddedSyncKeyRef.current === syncKey) return;
    lastEmbeddedSyncKeyRef.current = syncKey;
    onComplete(payload);
  }, [embedded, teamLifelines, teams, onComplete]);

  return (
    <div
      className={
        embedded
          ? "relative z-30 w-full flex justify-center px-4 md:px-10 xl:px-0 py-6 bg-primary-ivory"
          : "fixed inset-0 bg-black/80 flex items-center justify-center lg:p-[1%] p-4 z-50"
      }
    >
      <>
        <div
          // style={{ backgroundImage: `url(${MainHeroBanner})` }}
          className={`bg-center bg-cover bg-white-500 2xl:rounded-[2.083vw] md:rounded-[40px] sm:rounded-4xl rounded-2xl 2xl:p-[2.083vw] md:p-7 p-4 2xl:max-w-[47.917vw] xl:max-w-[920px] max-w-[700px] w-full relative hide-scroll ${embedded
            ? "overflow-visible"
            : "overflow-auto animate-[slideUp_0.3s_ease-out] 2xl:max-h-[calc(100dvh-1.042vw)] max-h-[calc(100dvh-20px)]"
            }`}
        >
          {/* Close Button */}
          {!embedded && (
            <button
              onClick={onClose}
              className="lg:w-[1.2vw] lg:h-[1.2vw] md:w-5 w-4 md:h-5 h-4 flex items-center justify-center cursor-pointer ms-auto hover:scale-110 transition-transform duration-300 outline-none absolute top-4 right-8"
            >
              <svg
                className="w-full h-full"
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M15.9474 12.1123C15.8856 12.0504 15.8856 11.9496 15.9474 11.8878L23.5029 4.3286C24.149 3.68231 24.149 2.63063 23.5029 1.98429L22.0051 0.48573C21.692 0.172468 21.2758 0 20.833 0C20.3902 0 19.9739 0.172468 19.6609 0.485683L12.1054 8.04493C12.0672 8.08316 12.0235 8.09122 11.9937 8.09122C11.9639 8.09122 11.9203 8.08321 11.8821 8.04493L4.32658 0.485683C4.01351 0.172468 3.59726 0 3.15445 0C2.71164 0 2.29544 0.172468 1.98236 0.485683L0.484547 1.98429C-0.161516 2.63063 -0.161516 3.68231 0.484547 4.3286L8.04001 11.8878C8.10185 11.9496 8.10185 12.0504 8.04001 12.1123L0.484547 19.6714C-0.161469 20.3178 -0.161469 21.3694 0.484547 22.0157L1.98236 23.5143C2.29544 23.8275 2.71168 24 3.15445 24C3.59721 24 4.01351 23.8275 4.32658 23.5143L11.882 15.9551C11.9203 15.9169 11.9638 15.9089 11.9937 15.9089C12.0235 15.9089 12.0671 15.9169 12.1053 15.9552L19.6608 23.5143C19.9739 23.8276 20.3901 24 20.8329 24C21.2758 24 21.692 23.8276 22.005 23.5143L23.5028 22.0158C24.1489 21.3695 24.1489 20.3178 23.5028 19.6715L15.9474 12.1123Z"
                  fill="#333938"
                />
              </svg>
            </button>
          )}

          {/* <button
          onClick={onClose}
          className="lg:w-[1.2vw] lg:h-[1.2vw] md:w-5 w-4 md:h-5 h-4 flex items-center justify-center cursor-pointer ms-auto hover:scale-110 transition-transform duration-300 outline-none"
        >
          <svg
            className="w-full h-full"
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M15.9474 12.1123C15.8856 12.0504 15.8856 11.9496 15.9474 11.8878L23.5029 4.3286C24.149 3.68231 24.149 2.63063 23.5029 1.98429L22.0051 0.48573C21.692 0.172468 21.2758 0 20.833 0C20.3902 0 19.9739 0.172468 19.6609 0.485683L12.1054 8.04493C12.0672 8.08316 12.0235 8.09122 11.9937 8.09122C11.9639 8.09122 11.9203 8.08321 11.8821 8.04493L4.32658 0.485683C4.01351 0.172468 3.59726 0 3.15445 0C2.71164 0 2.29544 0.172468 1.98236 0.485683L0.484547 1.98429C-0.161516 2.63063 -0.161516 3.68231 0.484547 4.3286L8.04001 11.8878C8.10185 11.9496 8.10185 12.0504 8.04001 12.1123L0.484547 19.6714C-0.161469 20.3178 -0.161469 21.3694 0.484547 22.0157L1.98236 23.5143C2.29544 23.8275 2.71168 24 3.15445 24C3.59721 24 4.01351 23.8275 4.32658 23.5143L11.882 15.9551C11.9203 15.9169 11.9638 15.9089 11.9937 15.9089C12.0235 15.9089 12.0671 15.9169 12.1053 15.9552L19.6608 23.5143C19.9739 23.8276 20.3901 24 20.8329 24C21.2758 24 21.692 23.8276 22.005 23.5143L23.5028 22.0158C24.1489 21.3695 24.1489 20.3178 23.5028 19.6715L15.9474 12.1123Z"
              fill="#333938"
            />
          </svg>
        </button> */}
          {/* Content */}

          <div className="flex  flex-col 2xl:gap-[1vw] gap-2 items-center justify-between w-full 2xl:mb-[1vw] mb-4">
            <h1 className="2xl:text-[1.563vw]/[1.7vw] xl:text-3xl/tight text-2xl/6 font-semibold text-primary-orange">
              {t("createGame.teamsLifeline")}
            </h1>
            <div className="flex items-center justify-center gap-2 w-full">

              {/* Tab Buttons */}
              <div className="flex w-full 2xl:max-w-[26.563vw] md:max-w-[518px] max-w-fit lg:p-[1%] p-1.5 bg-primary-ivory skew-x-[-10deg] 2xl:rounded-[0.26vw] rounded-[5px] relative">
                {/* Sliding background */}
                <div
                  className={`absolute lg:top-[0.3vw] top-1.5 lg:bottom-[0.3vw] bottom-1.5 2xl:border-[0.104vw] border-[1.5px] 2xl:rounded-[0.26vw] rounded-[5px] transition-all duration-300 ease-in-out bg-purple-fill bg-[#FFFBA1] border-primary-rose ${mode === "manual"
                    ? "lg:start-[0.4vw] start-1.5 lg:w-[calc(33.33%-0.5vw)] w-[calc(33.33%-8px)]"
                    : mode === "random"
                      ? "lg:start-[calc(33.33%+0.2vw)] start-[calc(33.33%+2px)] lg:w-[calc(33.33%-0.5vw)] w-[calc(33.33%-8px)]"
                      : "lg:start-[calc(66.66%+0.05vw)] start-[calc(66.66%+2px)] lg:w-[calc(33.33%-0.5vw)] w-[calc(33.33%-8px)]"
                    }`}
                ></div>

                <button
                  type="button"
                  onClick={() => {
                    if (mode === "random") {
                      // User explicitly switched from random to manual:
                      // clear all local lifeline state and prevent preset re-hydration.
                      skipPresetHydrationRef.current = true;
                    }
                    setMode("manual");
                    clearAllLifelineState();
                  }}
                  className={`flex-1 2xl:py-[0.65vw] py-1 2xl:mt-[0.208vw] mt-1 lg:px-[0.4vw] px-3 2xl:rounded-[0.26vw] rounded-[5px] skew-x-[10deg] font-bold 2xl:text-[0.95vw]/[1.2vw] md:text-base/tight text-sm/tight transition-all duration-200 text-center relative z-10 cursor-pointer focus:outline-none ${mode === "manual" ? "text-primary-rose border-primary-rose" : "text-black-300 border-transparent"
                    }`}
                >
                  <span>{t("createGame.manual")}</span>
                </button>
                <button
                  type="button"
                  onClick={() => {
                    // When coming from Play Again with preset lifelines,
                    // force immediate randomization on first click.
                    skipPresetHydrationRef.current = false;
                    presetLifelinesAppliedRef.current = false;
                    setMode("random");
                    assignRandomToAllTeams();
                  }}
                  className={`flex-1 2xl:py-[0.65vw] py-1 2xl:mt-[0.208vw] mt-1 lg:px-[0.4vw] px-3 2xl:rounded-[0.26vw] rounded-[5px] skew-x-[10deg] font-bold 2xl:text-[0.95vw]/none md:text-base/none text-sm/none transition-all duration-200 text-center relative z-10  cursor-pointer focus:outline-none ${mode === "random" ? "text-primary-rose border-primary-rose" : "text-black-300 border-transparent"
                    }`}
                >
                  <span>{t("createGame.randomize")}</span>
                </button>
                <button
                  type="button"
                  onClick={() => setMode("without")}
                  className={`flex-1 2xl:py-[0.65vw] py-1 2xl:mt-[0.208vw] mt-1 lg:px-[0.4vw] px-3 2xl:rounded-[0.26vw] rounded-[5px] skew-x-[10deg] font-bold 2xl:text-[0.95vw]/none md:text-base/none text-sm/none transition-all duration-200 text-center relative z-10 cursor-pointer focus:outline-none ${mode === "without" ? "text-primary-rose border-primary-rose" : "text-black-300 border-transparent"
                    }`}
                >
                  <span>{t("createGame.withoutLifelines") || "Without"}</span>
                </button>
              </div>
              {mode === "random" && (
                <button
                  type="button"
                  onClick={() => {
                    presetLifelinesAppliedRef.current = false;
                    assignRandomToAllTeams();
                  }}
                  className="2xl:w-[2.5vw] 2xl:h-[2.5vw] md:w-10 md:h-10 w-9 h-9 flex items-center justify-center skew-x-[-10deg] bg-primary-orange 2xl:border-[0.104vw] border-[1.5px] border-black-300 2xl:rounded-[0.26vw] rounded-[5px] 2xl:shadow-[0px_0.156vw_0px_#110706] shadow-[0px_2px_0px_#110706] transition-all duration-200 cursor-pointer hover:-translate-y-0.5 hover:scale-105"
                  title={t("createGame.refreshRandomLifelines") || "Refresh Random Lifelines"}
                >
                  <svg
                    className="2xl:w-[1.25vw] 2xl:h-[1.25vw] md:w-5 md:h-5 w-4 h-4 text-white skew-x-[10deg]"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2.5}
                      d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                    />
                  </svg>
                </button>
              )}
              {/* Refresh Button - Only visible in random mode */}
              {/* {mode === "random" && (
              <button
                type="button"
                onClick={assignRandomToAllTeams}
                className="2xl:w-[2.5vw] 2xl:h-[2.5vw] md:w-10 md:h-10 w-9 h-9 flex items-center justify-center skew-x-[-10deg] bg-primary-orange 2xl:border-[0.104vw] border-[1.5px] border-black-300 2xl:rounded-[0.26vw] rounded-[5px] 2xl:shadow-[0px_0.156vw_0px_#110706] shadow-[0px_2px_0px_#110706] transition-all duration-200 cursor-pointer hover:-translate-y-0.5 hover:scale-105"
                title={t("createGame.refreshRandomLifelines") || "Refresh Random Lifelines"}
              >
                <svg
                  className="2xl:w-[1.25vw] 2xl:h-[1.25vw] md:w-5 md:h-5 w-4 h-4 text-white skew-x-[10deg]"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2.5}
                    d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                  />
                </svg>
              </button>
            )} */}
            </div>

            {(lifelines ?? []).length > 0 && (
              <div className="flex flex-wrap justify-center gap-2 sm:gap-3 w-full 2xl:mt-[0.5vw] mt-2 px-1 overflow-visible">
                {(lifelines ?? []).map((lifeline) => (
                  <div
                    key={lifeline.id}
                    className="relative z-0 hover:z-[60] focus-within:z-[60] group"
                  >
                    <div
                      className="flex items-center justify-center 2xl:w-[3.2vw] w-11 h-11 2xl:h-[3.2vw] bg-white 2xl:rounded-[0.26vw] rounded-lg 2xl:border-[0.104vw] border-[1.5px] border-black-300 2xl:shadow-[0px_0.156vw_0px_#110706] shadow-[0px_2px_0px_#110706] cursor-pointer"
                      onClick={() => {
                        if (!lifeline.description) return;
                        setActiveLifelineDescriptionId((prev) => (prev === lifeline.id ? null : lifeline.id));
                      }}
                    >
                      <img
                        src={lifeline.image}
                        alt={lifeline.name}
                        className="w-8 h-8 2xl:w-[2.2vw] 2xl:h-[2.2vw] object-contain"
                      />
                    </div>
                    {lifeline.description ? (
                      <div
                        className={`absolute bottom-full left-1/2 -translate-x-1/2 mb-2 flex flex-col items-center transition-all duration-200 pointer-events-none z-[60] ${activeLifelineDescriptionId === lifeline.id ? "opacity-100 visible" : "opacity-0 invisible md:group-hover:opacity-100 md:group-hover:visible"}`}
                      >
                        <div
                          className="w-[min(14rem,48vw)] h-[min(14rem,48vw)] 2xl:w-[16rem] 2xl:h-[16rem] flex items-center justify-center overflow-y-auto overscroll-contain 2xl:p-[1.25vw] p-5 bg-primary-orange text-white 2xl:text-[1.2vw]/relaxed md:text-lg/relaxed text-base/relaxed font-bold text-center 2xl:rounded-[0.625vw] rounded-xl 2xl:border-[0.104vw] border-2 border-black-300 2xl:shadow-[0px_0.156vw_0px_#110706] shadow-[0px_2px_0px_#110706] whitespace-normal [scrollbar-width:thin]"
                        >
                          {lifeline.description}
                        </div>
                        <VscTriangleDown className="text-primary-orange 2xl:w-[1.35vw] w-7 2xl:h-[1.35vw] h-7 shrink-0 -mt-px" />
                      </div>
                    ) : null}
                  </div>
                ))}
              </div>
            )}
          </div>

          {mode !== "without" && (
            <>
              {/* Divider */}
              <div className="text-center 2xl:mb-[1vw] mb-4">
                <hr className="border-0 2xl:h-[0.053vw] h-[1px] w-full [background:linear-gradient(90deg,rgba(11,51,105,0)_0%,#7364FF_50%,rgba(11,51,105,0)_100%)]" />
              </div>

              {/* Teams Grid */}
              <div className="">
                <div className="grid sm:grid-cols-2 grid-cols-1 2xl:gap-[0.833vw] gap-3 2xl:mb-[1.25vw] mb-4">
                  {teamCards.map((team, index) => (
                    <TeamCard
                      key={`${team.title || "team"}-${index}`}
                      teamIndex={index}
                      title={team.title}
                      color={team.color}
                      items={teamLifelines[index] || []}
                      isManual={mode === "manual"}
                      onAdd={handleAdd}
                      onRemove={handleRemove}
                    />
                  ))}
                </div>

                {!embedded && (
                  <div className="flex justify-center">
                    <PrimaryBtn
                      className="!mb-0"
                      onClick={handleGoToGamePlay}
                      disabled={isCreatingGame || teams.length === 0}
                      isLoading={isCreatingGame}
                    >
                      {isCreatingGame ? (
                        <div className="flex items-center justify-center">
                          <div className="w-6 h-6 border-4 border-white border-t-transparent rounded-full animate-spin"></div>
                        </div>
                      ) : (
                        t("createGame.goToGamePlay")
                      )}
                    </PrimaryBtn>
                  </div>
                )}
              </div>
            </>
          )}
        </div>
        <LifelineSelectionModal
          isOpen={isSelectionModalOpen}
          onClose={() => {
            setIsSelectionModalOpen(false);
            setSelectedLifelineId(null);
            setSelectedLifelineIds([]);
            setCurrentSelection(null);
          }}
          lifelines={enabledLifelines}
          onSelectLifelineId={handleToggleLifelineSelection}
          selectedLifelineId={selectedLifelineId}
          onConfirm={handleConfirmLifeline}
          selectedLifelineIds={alreadyAssignedLifelineIds}
          allowMultiple={mode === "manual"}
          maxSelections={lifelinesPerTeam}
          currentlySelectedIds={mode === "manual" ? selectedLifelineIds : []}
        />
      </>
    </div>
  );
};

export default SpinWheelModal;
